// services/clientService.js
// Client Portal Service — Client management, messaging, appointments, intake forms
// Updated with firmId support for multi-tenancy

import { db } from '../firebase-config';
import { 
  collection, addDoc, getDocs, updateDoc, deleteDoc, doc, 
  query, where, serverTimestamp 
} from 'firebase/firestore';

// Helper: query with firmId fallback to userId
const queryWithFirmFallback = async (collectionName, userId, firmId, extraFilters = []) => {
  let results = [];
  
  // Try firmId first
  if (firmId) {
    let q = query(collection(db, collectionName), where('firmId', '==', firmId), ...extraFilters);
    const snapshot = await getDocs(q);
    results = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  }
  
  // No creator-field (userId-only) fallback: firm data is read ONLY through the
  // current firm's tenant-scoped query above. A userId-only query cannot be
  // proven against the active-membership rules and is denied outright; a
  // firm-constrained variant would be a strict subset of the query above.
  return results;
};

// ============================================================================
// CLIENTS CRUD
// ============================================================================

export const createClient = async (userId, clientData, firmId) => {
  try {
    const docRef = await addDoc(collection(db, 'clients'), {
      ...clientData,
      userId,
      firmId: firmId,
      status: clientData.status || 'active',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getClients = async (userId, filters = {}, firmId) => {
  try {
    let clients = await queryWithFirmFallback('clients', userId, firmId);

    if (filters.status && filters.status !== 'all') {
      clients = clients.filter(c => c.status === filters.status);
    }
    if (filters.search) {
      const s = filters.search.toLowerCase();
      clients = clients.filter(c =>
        c.name?.toLowerCase().includes(s) ||
        c.email?.toLowerCase().includes(s) ||
        c.phone?.includes(s) ||
        c.company?.toLowerCase().includes(s)
      );
    }

    clients.sort((a, b) => {
      const da = a.createdAt?.toDate?.() || new Date(0);
      const db2 = b.createdAt?.toDate?.() || new Date(0);
      return db2 - da;
    });

    return { success: true, clients };
  } catch (error) {
    return { success: false, error: error.message, clients: [] };
  }
};

export const updateClient = async (clientId, updates) => {
  try {
    await updateDoc(doc(db, 'clients', clientId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteClient = async (clientId) => {
  try {
    await deleteDoc(doc(db, 'clients', clientId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============================================================================
// CLIENT MESSAGES
// ============================================================================

export const sendClientMessage = async (userId, messageData, firmId) => {
  try {
    const docRef = await addDoc(collection(db, 'clientMessages'), {
      ...messageData,
      userId,
      firmId: firmId,
      read: false,
      createdAt: serverTimestamp()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getClientMessages = async (userId, clientId, firmId) => {
  try {
    let messages = [];

    if (firmId) {
      const q = query(
        collection(db, 'clientMessages'),
        where('firmId', '==', firmId),
        where('clientId', '==', clientId)
      );
      const snapshot = await getDocs(q);
      messages = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    }

    // No creator-field (userId-only) fallback when the firm query is empty:
    // it cannot be proven against the active-membership rules (denied outright,
    // turning "no messages yet" into an error), and a firm-constrained variant
    // would return the same empty set as the query above.

    messages.sort((a, b) => {
      const da = a.createdAt?.toDate?.() || new Date(0);
      const db2 = b.createdAt?.toDate?.() || new Date(0);
      return da - db2;
    });

    return { success: true, messages };
  } catch (error) {
    return { success: false, error: error.message, messages: [] };
  }
};

// ============================================================================
// APPOINTMENTS
// ============================================================================

export const createAppointment = async (userId, appointmentData, firmId) => {
  try {
    const docRef = await addDoc(collection(db, 'appointments'), {
      ...appointmentData,
      userId,
      firmId: firmId,
      status: 'scheduled',
      createdAt: serverTimestamp()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getAppointments = async (userId, filters = {}, firmId) => {
  try {
    let appointments = await queryWithFirmFallback('appointments', userId, firmId);

    if (filters.clientId) {
      appointments = appointments.filter(a => a.clientId === filters.clientId);
    }
    if (filters.status && filters.status !== 'all') {
      appointments = appointments.filter(a => a.status === filters.status);
    }

    appointments.sort((a, b) => {
      const da = new Date(a.date + ' ' + (a.time || '00:00'));
      const db2 = new Date(b.date + ' ' + (b.time || '00:00'));
      return da - db2;
    });

    return { success: true, appointments };
  } catch (error) {
    return { success: false, error: error.message, appointments: [] };
  }
};

export const updateAppointment = async (appointmentId, updates) => {
  try {
    await updateDoc(doc(db, 'appointments', appointmentId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteAppointment = async (appointmentId) => {
  try {
    await deleteDoc(doc(db, 'appointments', appointmentId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============================================================================
// CLIENT INTAKE FORMS
// ============================================================================

export const createIntakeForm = async (userId, formData, firmId) => {
  try {
    const docRef = await addDoc(collection(db, 'intakeForms'), {
      ...formData,
      userId,
      firmId: firmId,
      status: 'pending',
      createdAt: serverTimestamp()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getIntakeForms = async (userId, firmId) => {
  try {
    let forms = await queryWithFirmFallback('intakeForms', userId, firmId);
    forms.sort((a, b) => {
      const da = a.createdAt?.toDate?.() || new Date(0);
      const db2 = b.createdAt?.toDate?.() || new Date(0);
      return db2 - da;
    });
    return { success: true, forms };
  } catch (error) {
    return { success: false, error: error.message, forms: [] };
  }
};

export const updateIntakeForm = async (formId, updates) => {
  try {
    await updateDoc(doc(db, 'intakeForms', formId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const convertIntakeToClient = async (userId, intakeForm, firmId) => {
  try {
    const clientResult = await createClient(userId, {
      name: intakeForm.clientName,
      email: intakeForm.email,
      phone: intakeForm.phone,
      address: intakeForm.address,
      company: intakeForm.company || '',
      notes: intakeForm.legalIssue || '',
      source: 'intake_form'
    }, firmId);

    if (clientResult.success) {
      await updateIntakeForm(intakeForm.id, { 
        status: 'converted', 
        clientId: clientResult.id 
      });
    }

    return clientResult;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============================================================================
// CLIENT PAYMENTS
// ============================================================================

export const addClientPayment = async (userId, paymentData, firmId) => {
  try {
    const docRef = await addDoc(collection(db, 'clientPayments'), {
      ...paymentData,
      userId,
      firmId: firmId,
      createdAt: serverTimestamp()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getClientPayments = async (userId, clientId = null, firmId) => {
  try {
    let payments = await queryWithFirmFallback('clientPayments', userId, firmId);

    if (clientId) {
      payments = payments.filter(p => p.clientId === clientId);
    }

    payments.sort((a, b) => {
      const da = a.createdAt?.toDate?.() || new Date(0);
      const db2 = b.createdAt?.toDate?.() || new Date(0);
      return db2 - da;
    });

    return { success: true, payments };
  } catch (error) {
    return { success: false, error: error.message, payments: [] };
  }
};

export const updateClientPayment = async (paymentId, updates) => {
  try {
    await updateDoc(doc(db, 'clientPayments', paymentId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteClientPayment = async (paymentId) => {
  try {
    await deleteDoc(doc(db, 'clientPayments', paymentId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============================================================================
// CLIENT BILLING RECORDS
// ============================================================================

export const updateClientBilling = async (clientId, billingData) => {
  try {
    await updateDoc(doc(db, 'clients', clientId), {
      totalBilled: billingData.totalBilled || 0,
      totalPaid: billingData.totalPaid || 0,
      outstanding: billingData.outstanding || 0,
      lastPaymentDate: billingData.lastPaymentDate || null,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const calculateClientBalance = (payments, totalBilled) => {
  const totalPaid = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  const outstanding = (parseFloat(totalBilled) || 0) - totalPaid;
  return { totalPaid, outstanding: Math.max(outstanding, 0), totalBilled: parseFloat(totalBilled) || 0 };
};